/**
 * TitlePoint API Service
 * TpsService.asmx endpoint for property and title searches
 */

// TitlePoint credentials stored in sessionStorage
const CREDENTIALS_KEY = 'titlepoint_credentials';

// TitlePoint service endpoint
const TITLEPOINT_SERVICE_URL = 'https://titlepoint.com/TitlePointServices/TpsService.asmx';

/**
 * Get stored TitlePoint credentials
 */
export function getCredentials() {
  try {
    const stored = sessionStorage.getItem(CREDENTIALS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load TitlePoint credentials:', error);
  }

  return {
    username: '',
    password: '',
    company: '',
    department: '',
    titleOfficer: '',
    serviceUrl: TITLEPOINT_SERVICE_URL,
  };
}

/**
 * Set TitlePoint credentials
 */
export function setCredentials(username, password, serviceUrl, company, department, titleOfficer) {
  const credentials = {
    username,
    password,
    company: company || '',
    department: department || '',
    titleOfficer: titleOfficer || '',
    serviceUrl: serviceUrl || TITLEPOINT_SERVICE_URL,
    configured: true,
    lastUpdated: new Date().toISOString()
  };

  sessionStorage.setItem(CREDENTIALS_KEY, JSON.stringify(credentials));
  return credentials;
}

/**
 * Check if TitlePoint credentials are configured
 */
export function isConfigured() {
  const creds = getCredentials();
  return !!(creds.username && creds.password && creds.configured);
}

/**
 * Clear stored credentials
 */
export function clearCredentials() {
  sessionStorage.removeItem(CREDENTIALS_KEY);
}

/**
 * Build SOAP request for CreateService (property search)
 */
export function buildCreateServiceXML(searchParams, credentials) {
  const {
    address = '',
    city = '',
    state = 'MI',
    zip = '',
    county = '',
    apn = ''
  } = searchParams;

  return `<?xml version="1.0" encoding="utf-8"?>
<soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
               xmlns:xsd="http://www.w3.org/2001/XMLSchema"
               xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">
  <soap:Body>
    <CreateService xmlns="http://www.TitlePoint.com">
      <userID>${escapeXml(credentials.username)}</userID>
      <password>${escapeXml(credentials.password)}</password>
      <company>${escapeXml(credentials.company)}</company>
      <department>${escapeXml(credentials.department)}</department>
      <titleOfficer>${escapeXml(credentials.titleOfficer)}</titleOfficer>
      <propertyAddress>${escapeXml(address)}</propertyAddress>
      <propertyCity>${escapeXml(city)}</propertyCity>
      <propertyState>${escapeXml(state)}</propertyState>
      <propertyZip>${escapeXml(zip)}</propertyZip>
      <propertyCounty>${escapeXml(county)}</propertyCounty>
      <propertyAPN>${escapeXml(apn)}</propertyAPN>
      <serviceType>TitleSearch</serviceType>
    </CreateService>
  </soap:Body>
</soap:Envelope>`;
}

/**
 * Build SOAP request for CreateService3
 * Uses semicolon-delimited parameter string as per TitlePoint documentation
 */
export function buildCreateService3XML(searchParams, credentials) {
  const {
    address = '',
    city = '',
    state = 'MI',
    zip = '',
    county = '',
    apn = '',
    customerRef = '',
    orderNo = '',
    serviceType = 'TitlePoint.Geo.Property'
  } = searchParams;

  // Build semicolon-delimited parameter string
  // Format: Key=Value;Key=Value (see TitlePoint Legal Formats documentation)
  const paramParts = [];
  if (address) paramParts.push(`Address=${address}`);
  if (city) paramParts.push(`City=${city}`);
  if (zip) paramParts.push(`Zip=${zip}`);
  if (apn) paramParts.push(`APN=${apn}`);

  const parametersString = paramParts.join(';');

  return `<?xml version="1.0" encoding="utf-8"?>
<soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
               xmlns:xsd="http://www.w3.org/2001/XMLSchema"
               xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">
  <soap:Body>
    <CreateService3 xmlns="http://www.TitlePoint.com">
      <userID>${escapeXml(credentials.username)}</userID>
      <password>${escapeXml(credentials.password)}</password>
      <orderNo>${escapeXml(orderNo)}</orderNo>
      <customerRef>${escapeXml(customerRef)}</customerRef>
      <serviceType>${escapeXml(serviceType)}</serviceType>
      <parameters>${escapeXml(parametersString)}</parameters>
      <company>${escapeXml(credentials.company)}</company>
      <department>${escapeXml(credentials.department)}</department>
      <titleOfficer>${escapeXml(credentials.titleOfficer)}</titleOfficer>
      <state>${escapeXml(state)}</state>
      <county>${escapeXml(county)}</county>
      <orderComment></orderComment>
      <starterRemarks></starterRemarks>
    </CreateService3>
  </soap:Body>
</soap:Envelope>`;
}

/**
 * Build SOAP request for ResolveProperties
 */
export function buildResolvePropertiesXML(searchParams, credentials) {
  const {
    address = '',
    city = '',
    state = 'MI',
    zip = '',
    county = '',
    apn = ''
  } = searchParams;

  return `<?xml version="1.0" encoding="utf-8"?>
<soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
               xmlns:xsd="http://www.w3.org/2001/XMLSchema"
               xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">
  <soap:Body>
    <ResolveProperties xmlns="http://www.TitlePoint.com">
      <userID>${escapeXml(credentials.username)}</userID>
      <password>${escapeXml(credentials.password)}</password>
      <company>${escapeXml(credentials.company)}</company>
      <department>${escapeXml(credentials.department)}</department>
      <titleOfficer>${escapeXml(credentials.titleOfficer)}</titleOfficer>
      <propertyParameterSets>
        <PropertyParameterSet>
          <PropertyAddress1>${escapeXml(address)}</PropertyAddress1>
          <PropertyCity>${escapeXml(city)}</PropertyCity>
          <PropertyState>${escapeXml(state)}</PropertyState>
          <PropertyZipCode>${escapeXml(zip)}</PropertyZipCode>
          <PropertyCounty>${escapeXml(county)}</PropertyCounty>
          <PropertyAPN>${escapeXml(apn)}</PropertyAPN>
        </PropertyParameterSet>
      </propertyParameterSets>
    </ResolveProperties>
  </soap:Body>
</soap:Envelope>`;
}

/**
 * Build SOAP request for GetRequestSummaries
 * Used to check the status of an asynchronous CreateService request
 */
export function buildGetRequestSummariesXML(requestId, credentials) {
  return `<?xml version="1.0" encoding="utf-8"?>
<soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
               xmlns:xsd="http://www.w3.org/2001/XMLSchema"
               xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">
  <soap:Body>
    <GetRequestSummaries xmlns="http://www.TitlePoint.com">
      <userID>${escapeXml(credentials.username)}</userID>
      <password>${escapeXml(credentials.password)}</password>
      <company>${escapeXml(credentials.company)}</company>
      <requestId>${escapeXml(requestId)}</requestId>
    </GetRequestSummaries>
  </soap:Body>
</soap:Envelope>`;
}

/**
 * Build SOAP request for GetResultById (property search results)
 */
export function buildGetResultByIdXML(resultId, credentials) {
  return `<?xml version="1.0" encoding="utf-8"?>
<soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
               xmlns:xsd="http://www.w3.org/2001/XMLSchema"
               xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">
  <soap:Body>
    <GetResultById xmlns="http://www.TitlePoint.com">
      <userID>${escapeXml(credentials.username)}</userID>
      <password>${escapeXml(credentials.password)}</password>
      <resultID>${escapeXml(resultId)}</resultID>
    </GetResultById>
  </soap:Body>
</soap:Envelope>`;
}

/**
 * Poll GetRequestSummaries until the request is complete
 * Returns the request summary data when complete
 */
export async function pollRequestStatus(requestId, credentials, maxAttempts = 30, intervalMs = 2000) {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const soapXML = buildGetRequestSummariesXML(requestId, credentials);

    try {
      const response = await callTitlePointAPI(
        credentials.serviceUrl,
        soapXML,
        credentials,
        'http://www.TitlePoint.com/GetRequestSummaries'
      );

      const parsed = parseSOAPResponse(response.responseXML || response.rawResponse);

      if (!parsed.success) {
        throw new Error(parsed.error);
      }

      // Check if request is complete
      // The actual response structure may vary - adjust based on TitlePoint API docs
      const summaries = parsed.data;

      // If complete, return summaries
      // If not complete, wait and try again
      if (summaries && summaries.isComplete) {
        return summaries;
      }

      // Wait before next attempt
      if (attempt < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, intervalMs));
      }
    } catch (error) {
      if (attempt === maxAttempts) {
        throw error;
      }
      // Wait before retry on error
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
  }

  throw new Error(`Request ${requestId} did not complete after ${maxAttempts} attempts`);
}

/**
 * Execute complete TitlePoint workflow:
 * 1. Create service request
 * 2. Poll for completion
 * 3. Retrieve results
 */
export async function executeSearchWorkflow(searchParams, credentials) {
  // Step 1: Create service request
  const createXML = buildCreateService3XML(searchParams, credentials);
  const createResponse = await callTitlePointAPI(
    credentials.serviceUrl,
    createXML,
    credentials,
    'http://www.TitlePoint.com/CreateService3'
  );

  const createParsed = parseSOAPResponse(createResponse.responseXML || createResponse.rawResponse);

  if (!createParsed.success) {
    throw new Error(`CreateService failed: ${createParsed.error}`);
  }

  // Extract requestId and orderId from response
  const requestId = createParsed.data.requestId || createParsed.data.RequestID;
  const orderId = createParsed.data.orderId || createParsed.data.OrderID;

  if (!requestId) {
    throw new Error('No requestId returned from CreateService');
  }

  // Step 2: Poll for completion
  const summaries = await pollRequestStatus(requestId, credentials);

  // Step 3: Retrieve results for each completed search
  const results = [];
  if (summaries.searches && Array.isArray(summaries.searches)) {
    for (const search of summaries.searches) {
      if (search.resultId) {
        const resultXML = buildGetResultByIdXML(search.resultId, credentials);
        const resultResponse = await callTitlePointAPI(
          credentials.serviceUrl,
          resultXML,
          credentials,
          'http://www.TitlePoint.com/GetResultById'
        );

        const resultParsed = parseSOAPResponse(resultResponse.responseXML || resultResponse.rawResponse);
        results.push({
          searchId: search.id,
          resultId: search.resultId,
          data: resultParsed
        });
      }
    }
  }

  return {
    requestId,
    orderId,
    summaries,
    results
  };
}
/**
 * Build authentication test SOAP request
 */
export function buildAuthTestXML(credentials) {
  return `<?xml version="1.0" encoding="utf-8"?>
<soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
               xmlns:xsd="http://www.w3.org/2001/XMLSchema"
               xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">
  <soap:Body>
    <GetActiveUserOrders xmlns="http://www.TitlePoint.com">
      <userID>${escapeXml(credentials.username)}</userID>
      <password>${escapeXml(credentials.password)}</password>
      <company>${escapeXml(credentials.company)}</company>
    </GetActiveUserOrders>
  </soap:Body>
</soap:Envelope>`;
}

/**
 * Get sample SOAP templates for testing
 */
export function getSampleTemplates(credentials) {
  return {
    authTest: {
      name: 'Authentication Test - Get User Orders',
      description: 'Test credentials by retrieving user orders',
      soapAction: 'http://www.TitlePoint.com/GetActiveUserOrders',
      xml: buildAuthTestXML(credentials)
    },
    resolveProperties: {
      name: 'Resolve Properties',
      description: 'Look up property details',
      soapAction: 'http://www.TitlePoint.com/ResolveProperties',
      xml: buildResolvePropertiesXML({
        address: '123 Main St',
        city: 'Grand Rapids',
        state: 'MI',
        zip: '49503',
        county: 'Kent'
      }, credentials)
    },
    createService: {
      name: 'Create Title Search',
      description: 'Create a new title search service',
      soapAction: 'http://www.TitlePoint.com/CreateService',
      xml: buildCreateServiceXML({
        address: '123 Main St',
        city: 'Grand Rapids',
        state: 'MI',
        zip: '49503',
        county: 'Kent'
      }, credentials)
    },
    createService3: {
      name: 'Create Service (Simplified)',
      description: 'CreateService3 with parameters object',
      soapAction: 'http://www.TitlePoint.com/CreateService3',
      xml: buildCreateService3XML({
        address: '123 Main St',
        city: 'Grand Rapids',
        state: 'MI',
        zip: '49503',
        county: 'Kent'
      }, credentials)
    }
    ,
    getRequestSummaries: {
      name: 'Get Request Status',
      description: 'Check status of asynchronous request',
      soapAction: 'http://www.TitlePoint.com/GetRequestSummaries',
      xml: buildGetRequestSummariesXML('12345', credentials)
    },
    getResultById: {
      name: 'Get Search Results',
      description: 'Retrieve results for completed search',
      soapAction: 'http://www.TitlePoint.com/GetResultById',
      xml: buildGetResultByIdXML('67890', credentials)
    }
  };
}

/**
 * Escape XML special characters
 */
function escapeXml(unsafe) {
  if (!unsafe) return '';
  return String(unsafe)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Parse SOAP XML response
 */
export function parseSOAPResponse(xmlString) {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

    // Check for SOAP fault
    const fault = xmlDoc.getElementsByTagName('soap:Fault')[0] ||
                  xmlDoc.getElementsByTagName('Fault')[0] ||
                  xmlDoc.getElementsByTagNameNS('*', 'Fault')[0];

    if (fault) {
      const faultCode = fault.getElementsByTagName('faultcode')[0]?.textContent ||
                        fault.getElementsByTagNameNS('*', 'faultcode')[0]?.textContent || 'Unknown';
      const faultString = fault.getElementsByTagName('faultstring')[0]?.textContent ||
                          fault.getElementsByTagNameNS('*', 'faultstring')[0]?.textContent || 'Unknown error';

      return {
        success: false,
        error: `SOAP Fault: ${faultCode} - ${faultString}`,
        rawXML: xmlString
      };
    }

    // Extract response body
    const body = xmlDoc.getElementsByTagName('soap:Body')[0] ||
                 xmlDoc.getElementsByTagName('Body')[0] ||
                 xmlDoc.getElementsByTagNameNS('*', 'Body')[0];

    if (!body) {
      return {
        success: false,
        error: 'Invalid SOAP response: No body found',
        rawXML: xmlString
      };
    }

    return {
      success: true,
      data: xmlToJson(body),
      rawXML: xmlString
    };

  } catch (error) {
    return {
      success: false,
      error: `Failed to parse XML: ${error.message}`,
      rawXML: xmlString
    };
  }
}

/**
 * Simple XML to JSON converter
 */
function xmlToJson(xml) {
  const obj = {};

  if (xml.nodeType === 1) { // Element node
    if (xml.attributes.length > 0) {
      obj['@attributes'] = {};
      for (let i = 0; i < xml.attributes.length; i++) {
        const attribute = xml.attributes.item(i);
        obj['@attributes'][attribute.nodeName] = attribute.nodeValue;
      }
    }
  } else if (xml.nodeType === 3) { // Text node
    return xml.nodeValue;
  }

  if (xml.hasChildNodes()) {
    for (let i = 0; i < xml.childNodes.length; i++) {
      const child = xml.childNodes.item(i);
      const nodeName = child.nodeName;

      if (typeof obj[nodeName] === 'undefined') {
        const childValue = xmlToJson(child);
        obj[nodeName] = childValue;
      } else {
        if (typeof obj[nodeName].push === 'undefined') {
          const old = obj[nodeName];
          obj[nodeName] = [];
          obj[nodeName].push(old);
        }
        obj[nodeName].push(xmlToJson(child));
      }
    }
  }

  // If this element only has text content (no child elements), return just the text
  // This handles cases like <RequestID>778865917</RequestID> returning "778865917" instead of {#text: "778865917"}
  const keys = Object.keys(obj);
  if (keys.length === 1 && keys[0] === '#text') {
    return obj['#text'];
  }

  return obj;
}

/**
 * Call TitlePoint API via backend proxy
 */
export async function callTitlePointAPI(endpoint, soapXML, credentials, soapAction) {
  const response = await fetch('/api/titlepoint/request', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      endpoint: endpoint || credentials.serviceUrl || TITLEPOINT_SERVICE_URL,
      soapXML,
      soapAction: soapAction || '',
      credentials
    })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// Legacy export for backwards compatibility - now uses CreateService3
export function buildPropertySearchXML(searchParams, credentials) {
  return buildCreateService3XML(searchParams, credentials);
}
